/**
 * Admin › Spend (`/admin/spend`): credits used and the top workspaces by
 * credits, then the platform-wide daily usage table, spend by model ×
 * pipeline × client, and the credit-grant report by reason. The attention
 * strip that used to sit here is on Systems.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminStatsOverview, CreditGrantReport } from "@yagni/shared";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  Coins,
  DollarSign,
  Gift,
  Server,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  ChartCard,
  DailyUsage,
  LoadingSpinner,
  SectionHeader,
  StatCard,
  StatsWindow,
  Table,
  Td,
  Th,
  WindowToggle,
  formatCost,
  formatCostShort,
  formatCredits,
  formatCreditsCompact,
  formatDayTick,
  formatDelta,
  formatTokens,
  workspacePath,
} from "./shared.js";

/** Spend: the credits read (formerly Overview's Pulse) above the spend tables. */
export function SpendPage() {
  return (
    <div className="space-y-8">
      <PulsePanel />
      <SpendPanel />
    </div>
  );
}

/* ─── Pulse ─────────────────────────────────────────────────── */

function PulsePanel() {
  const [windowDays, setWindowDays] = useState<StatsWindow>(30);
  const [stats, setStats] = useState<AdminStatsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetch(`/api/admin/stats/overview?days=${windowDays}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as AdminStatsOverview;
      })
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(`The overview failed to load: ${err instanceof Error ? err.message : String(err)}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const creditsSeries = useMemo(
    () => (stats ? stats.creditsDaily.map((r) => ({ day: r.day, daily: r.creditsUsed })) : []),
    [stats],
  );

  const workspacesSeries = useMemo(() => {
    if (!stats) return [];
    let running = stats.totals.workspacesBaseline;
    return stats.workspacesDaily.map((r) => {
      running += r.createdCount;
      return { day: r.day, total: running, newCount: r.createdCount };
    });
  }, [stats]);

  // Thin x-axis for longer windows so labels don't overlap.
  const xTickInterval = windowDays === 90 ? 13 : windowDays === 30 ? 4 : 0;

  if (loading) return <LoadingSpinner />;
  if (loadError || !stats) {
    return (
      <div
        role="alert"
        className="rounded-[var(--radius-card)] border-2 p-4 text-sm"
        style={{ borderColor: "var(--honesty-border)", background: "var(--honesty-bg)", color: "var(--text-secondary)" }}
      >
        {loadError ?? "The overview failed to load."}
      </div>
    );
  }

  const top = stats.topWorkspaces ?? [];
  const remainder = stats.topWorkspacesRemainder ?? { workspaceCount: 0, creditsUsed: 0 };
  const activeWorkspaces = top.length + remainder.workspaceCount;
  const maxCredits = Math.max(top[0]?.creditsUsed ?? 0, remainder.creditsUsed, 1);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <WindowToggle value={windowDays} onChange={setWindowDays} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label={`Credits used (${windowDays}d)`}
          value={formatCreditsCompact(stats.totals.creditsUsed)}
          icon={<Coins size={16} style={{ color: "var(--accent)" }} />}
        />
        <StatCard
          label={`LLM cost (${windowDays}d)`}
          value={formatCostShort(stats.totals.costMillicents)}
          sub="what the models actually cost us"
          icon={<DollarSign size={16} style={{ color: "var(--success)" }} />}
        />
        <StatCard
          label={`Active workspaces (${windowDays}d)`}
          value={activeWorkspaces}
          sub={`of ${stats.totals.workspacesTotal} used credits`}
          icon={<Server size={16} style={{ color: "var(--info)" }} />}
        />
        <StatCard
          label={`New workspaces (${windowDays}d)`}
          value={stats.totals.workspacesCreated}
          icon={<TrendingUp size={16} style={{ color: "var(--info)" }} />}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-4">
        <div
          className="rounded-[var(--radius-card)] p-4"
          style={{ background: "var(--print-bg)", border: "1px solid var(--print-border)", boxShadow: "var(--shadow-print)" }}
        >
          <SectionHeader
            title={`Top workspaces by credits, ${windowDays}d`}
            icon={<Coins size={16} style={{ color: "var(--accent)" }} />}
            action={
              <span className="text-[11px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                share of {formatCreditsCompact(stats.totals.creditsUsed)}
              </span>
            }
          />
          {top.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              No credits consumed in this window.
            </p>
          ) : (
            <ol className="m-0 p-0 list-none flex flex-col gap-1.5" data-testid="top-workspaces">
              {top.map((w, i) => {
                const delta = formatDelta(w.creditsUsed, w.priorCreditsUsed);
                return (
                  <li key={w.workspaceId}>
                    <Link
                      to={workspacePath(w.workspaceId, "credits")}
                      className="grid items-center gap-3 px-1.5 py-1 rounded-md no-underline hover:bg-[var(--accent-subtle)]"
                      style={{
                        gridTemplateColumns: "18px minmax(0, 170px) minmax(0, 1fr) 64px 56px",
                        color: "var(--text-primary)",
                      }}
                    >
                      <span className="text-[10.5px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12.5px] font-medium truncate">{w.name}</span>
                        <span className="block text-[10px] truncate" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                          {w.slug}
                          {w.internal ? " · internal" : ""}
                        </span>
                      </span>
                      <span
                        className="h-4 rounded-r overflow-hidden relative"
                        style={{ background: "var(--accent-subtle)" }}
                        aria-hidden
                      >
                        <span
                          className="absolute inset-y-0 left-0 rounded-r"
                          style={{
                            width: `${Math.max(2, (w.creditsUsed / maxCredits) * 100)}%`,
                            background: w.internal ? "var(--text-tertiary)" : "var(--accent)",
                          }}
                        />
                      </span>
                      <span className="text-right text-[12px] tabular-nums" style={{ fontFamily: "var(--font-mono)" }}>
                        {formatCreditsCompact(w.creditsUsed)}
                      </span>
                      <span
                        className="text-right text-[10.5px] tabular-nums"
                        style={{
                          fontFamily: "var(--font-mono)",
                          color:
                            delta.tone === "up"
                              ? "var(--success)"
                              : delta.tone === "down"
                                ? "var(--error)"
                                : "var(--text-tertiary)",
                        }}
                        title={`${formatCreditsCompact(w.priorCreditsUsed)} in the prior ${windowDays} days`}
                      >
                        {delta.label}
                      </span>
                    </Link>
                  </li>
                );
              })}
              {remainder.workspaceCount > 0 && (
                <li
                  className="grid items-center gap-3 px-1.5 py-1"
                  style={{ gridTemplateColumns: "18px minmax(0, 170px) minmax(0, 1fr) 64px 56px", color: "var(--text-secondary)" }}
                >
                  <span />
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-medium truncate">Everything else</span>
                    <span className="block text-[10px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                      {remainder.workspaceCount} workspace{remainder.workspaceCount === 1 ? "" : "s"}
                    </span>
                  </span>
                  <span className="h-4 rounded-r overflow-hidden relative" style={{ background: "var(--accent-subtle)" }} aria-hidden>
                    <span
                      className="absolute inset-y-0 left-0 rounded-r"
                      style={{ width: `${Math.max(2, (remainder.creditsUsed / maxCredits) * 100)}%`, background: "var(--text-tertiary)" }}
                    />
                  </span>
                  <span className="text-right text-[12px] tabular-nums" style={{ fontFamily: "var(--font-mono)" }}>
                    {formatCreditsCompact(remainder.creditsUsed)}
                  </span>
                  <span />
                </li>
              )}
            </ol>
          )}
          <div className="flex items-center justify-between mt-3 text-[11.5px]" style={{ color: "var(--text-tertiary)" }}>
            <span>Grey bars are internal. Change is against the prior {windowDays} days.</span>
            <Link to="/admin/workspaces" className="font-medium">
              All workspaces →
            </Link>
          </div>
        </div>

        <ChartCard
          title="Daily credit consumption"
          subtitle={`Credits spent per day, last ${windowDays} days`}
          icon={<Coins size={16} style={{ color: "var(--accent)" }} />}
        >
          <BarChart data={creditsSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
            <XAxis
              dataKey="day"
              tickFormatter={formatDayTick}
              interval={xTickInterval}
              tick={{ fill: "var(--text-tertiary)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "var(--border)" }}
            />
            <YAxis
              tickFormatter={(v: number) => v.toFixed(0)}
              tick={{ fill: "var(--text-tertiary)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "var(--border)" }}
              width={44}
            />
            <ReTooltip
              cursor={{ fill: "rgba(var(--ink-rgb), 0.05)" }}
              contentStyle={{
                background: "var(--print-bg-solid)",
                border: "1px solid var(--print-border)",
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={(label) => formatDayTick(String(label))}
              formatter={(v) => [formatCredits(Number(v)), "Credits"]}
            />
            <Bar dataKey="daily" fill="var(--accent)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartCard>
      </div>

      <ChartCard
        title="Cumulative workspaces"
        subtitle={`Total workspaces over time (baseline ${stats.totals.workspacesBaseline})`}
        icon={<Server size={16} style={{ color: "var(--info)" }} />}
      >
        <LineChart data={workspacesSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={formatDayTick}
            interval={xTickInterval}
            tick={{ fill: "var(--text-tertiary)", fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: "var(--border)" }}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: "var(--text-tertiary)", fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: "var(--border)" }}
            width={36}
          />
          <ReTooltip
            contentStyle={{
              background: "var(--print-bg-solid)",
              border: "1px solid var(--print-border)",
              borderRadius: 6,
              fontSize: 12,
            }}
            labelFormatter={(label) => formatDayTick(String(label))}
            formatter={(v, _name, item) => {
              const newCount = (item?.payload as { newCount?: number } | undefined)?.newCount ?? 0;
              return [`${Number(v)}${newCount > 0 ? ` (+${newCount} today)` : ""}`, "Workspaces"];
            }}
          />
          <Line type="monotone" dataKey="total" stroke="var(--info)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        </LineChart>
      </ChartCard>
    </div>
  );
}

/* ─── Spend ─────────────────────────────────────────────────── */

interface ModelPipelineUsage {
  model: string;
  agent_id: string;
  call_count: string;
  prompt_tokens: string;
  cache_read_tokens: string;
  completion_tokens: string;
  cost_millicents: string;
  sell_millicents: string | null;
  workspaces: string;
}

/** Provider path prefixes carry no signal in a table; keep the model name. */
function shortModelName(model: string): string {
  return model.replace(/^accounts\/fireworks\/models\//, "").replace(/^[a-z-]+\//, "");
}

/**
 * Coding client behind a `yagni_code_proxy:*` agent_id (browser-side mirror
 * of the backend's `clientForCaller` — exact caller match, boost-tolerant).
 * Non-proxy pipelines are not coding clients and show a dash.
 */
function codingClientFor(agentId: string): string {
  if (agentId !== "yagni_code_proxy" && !agentId.startsWith("yagni_code_proxy:")) return "—";
  let caller = agentId === "yagni_code_proxy" ? "" : agentId.slice("yagni_code_proxy:".length);
  if (caller.endsWith(":boost")) caller = caller.slice(0, -":boost".length);
  if (caller === "claude-code") return "Claude Code";
  if (caller === "codex") return "Codex";
  return "YAGNI Code";
}

const CREDIT_REASON_LABELS: Record<string, string> = {
  beta_feedback: "Beta feedback",
  goodwill: "Goodwill",
  internal: "Internal",
  investor: "Investor",
  marketing: "Marketing",
};

const CREDIT_REASON_COLORS: Record<string, string> = {
  beta_feedback: "var(--accent)",
  goodwill: "var(--success)",
  internal: "var(--text-tertiary)",
  investor: "var(--info)",
  marketing: "var(--warning)",
};

function SpendPanel() {
  const [daily, setDaily] = useState<DailyUsage[]>([]);
  const [models, setModels] = useState<ModelPipelineUsage[]>([]);
  const [modelDays, setModelDays] = useState<1 | 7 | 30>(1);
  const [report, setReport] = useState<CreditGrantReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch("/api/admin/usage/daily?days=30")
        .then((r) => (r.ok ? (r.json() as Promise<DailyUsage[]>) : []))
        .catch(() => []),
      fetch("/api/admin/credit-grants/report")
        .then((r) => (r.ok ? (r.json() as Promise<CreditGrantReport>) : null))
        .catch(() => null),
    ])
      .then(([d, rep]) => {
        setDaily(Array.isArray(d) ? d : []);
        setReport(rep);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch(`/api/admin/usage/models?days=${modelDays}`)
      .then((r) => r.json())
      .then((m: unknown) => setModels(Array.isArray(m) ? (m as ModelPipelineUsage[]) : []))
      .catch(() => setModels([]));
  }, [modelDays]);

  if (loading) return <LoadingSpinner />;

  const totalTurns = daily.reduce((s, d) => s + Number(d.turn_count), 0);
  const totalTokens = daily.reduce((s, d) => s + Number(d.prompt_tokens) + Number(d.completion_tokens), 0);
  const totalCost = daily.reduce((s, d) => s + Number(d.cost_millicents), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Turns (30d)" value={totalTurns} icon={<Zap size={16} style={{ color: "var(--warning)" }} />} />
        <StatCard label="Total Tokens (30d)" value={formatTokens(totalTokens)} icon={<Activity size={16} style={{ color: "var(--success)" }} />} />
        <StatCard label="Total Cost (30d)" value={formatCost(totalCost)} icon={<Activity size={16} style={{ color: "var(--info)" }} />} />
      </div>

      <div>
        <SectionHeader title="Spend by model × pipeline" icon={<Activity size={16} style={{ color: "var(--info)" }} />} />
        <div className="flex items-center gap-2 mb-2">
          {([1, 7, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setModelDays(d)}
              className="px-2 py-1 rounded text-[12px] border"
              style={{
                borderColor: "var(--print-border)",
                background: modelDays === d ? "var(--accent)" : "transparent",
                color: modelDays === d ? "var(--text-on-accent)" : "var(--text-secondary)",
              }}
            >
              {d === 1 ? "Today (24h)" : `${d} days`}
            </button>
          ))}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Model</Th>
              <Th>Pipeline</Th>
              <Th>Client</Th>
              <Th>Calls</Th>
              <Th>Fresh In</Th>
              <Th>Cache Reads</Th>
              <Th>Out</Th>
              <Th>Our Cost</Th>
              <Th>Charged</Th>
              <Th>Workspaces</Th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={`${m.model}:${m.agent_id}`}>
                <Td>{shortModelName(m.model)}</Td>
                <Td>{m.agent_id}</Td>
                <Td>{codingClientFor(m.agent_id)}</Td>
                <Td>{Number(m.call_count).toLocaleString()}</Td>
                <Td>{formatTokens(Number(m.prompt_tokens))}</Td>
                <Td>{formatTokens(Number(m.cache_read_tokens))}</Td>
                <Td>{formatTokens(Number(m.completion_tokens))}</Td>
                <Td>{formatCost(Number(m.cost_millicents))}</Td>
                <Td>{m.sell_millicents !== null ? formatCost(Number(m.sell_millicents)) : "—"}</Td>
                <Td>{m.workspaces}</Td>
              </tr>
            ))}
            {models.length === 0 && (
              <tr>
                <Td>No dispatches in this window</Td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {report && (
        <div
          className="rounded-[var(--radius-card)] p-4"
          style={{ background: "var(--print-bg)", border: "1px solid var(--print-border)", boxShadow: "var(--shadow-print)" }}
        >
          <SectionHeader
            title="Credit grants by reason"
            icon={<Gift size={16} style={{ color: "var(--accent)" }} />}
            action={
              <span className="text-[11px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                {formatCredits(report.totals.consumedCredits)} consumed / {formatCredits(report.totals.grantedCredits)} granted ·{" "}
                {report.totals.grantCount} grants
              </span>
            }
          />
          <div className="flex flex-col gap-2">
            {report.byReason.map((r) => {
              const pct = report.totals.grantedCredits > 0 ? (r.grantedCredits / report.totals.grantedCredits) * 100 : 0;
              const consumedPct = r.grantedCredits > 0 ? (r.consumedCredits / r.grantedCredits) * 100 : 0;
              return (
                <div key={r.reason} className="flex items-center gap-3">
                  <div className="text-xs w-28" style={{ color: "var(--text-secondary)" }}>
                    {CREDIT_REASON_LABELS[r.reason] ?? r.reason}
                  </div>
                  <div className="flex-1 h-5 rounded overflow-hidden relative" style={{ background: "var(--bg-base)" }}>
                    <div
                      className="absolute top-0 left-0 h-full"
                      style={{ width: `${pct}%`, background: CREDIT_REASON_COLORS[r.reason] ?? "var(--accent)", opacity: 0.3 }}
                    />
                    <div
                      className="absolute top-0 left-0 h-full"
                      style={{ width: `${(pct * consumedPct) / 100}%`, background: CREDIT_REASON_COLORS[r.reason] ?? "var(--accent)" }}
                    />
                  </div>
                  <div className="text-xs w-32 text-right tabular-nums" style={{ color: "var(--text-primary)" }}>
                    {formatCredits(r.consumedCredits)} / {formatCredits(r.grantedCredits)}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11.5px] mt-3" style={{ color: "var(--text-tertiary)" }}>
            Grants are made from a workspace's Credits tab.
          </p>
        </div>
      )}

      <div>
        <SectionHeader title="Daily Usage" icon={<Activity size={16} style={{ color: "var(--success)" }} />} />
        <Table>
          <thead>
            <tr>
              <Th>Day</Th>
              <Th>Turns</Th>
              <Th>Prompt Tokens</Th>
              <Th>Completion Tokens</Th>
              <Th>Cost</Th>
              <Th>Active Workspaces</Th>
            </tr>
          </thead>
          <tbody>
            {daily.map((d) => (
              <tr key={d.day}>
                {/* The day is a UTC calendar date; new Date().toLocaleDateString()
                    would shift it to the previous day west of UTC. */}
                <Td>{String(d.day).slice(0, 10)}</Td>
                <Td>{d.turn_count}</Td>
                <Td>{formatTokens(Number(d.prompt_tokens))}</Td>
                <Td>{formatTokens(Number(d.completion_tokens))}</Td>
                <Td>{formatCost(Number(d.cost_millicents))}</Td>
                <Td>{d.active_workspaces}</Td>
              </tr>
            ))}
            {daily.length === 0 && (
              <tr>
                <Td>No usage data yet</Td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
